import { isCompanyAdmin } from '../Account/account.utils'

export function formatWhen(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

export function showShopLegalNav(user) {
  return isCompanyAdmin(user)
}

export const LEGAL_TYPES = [
  'terms-of-service',
  'privacy-policy',
  'shipping-policy',
  'refund-and-return-policy',
  'accessibility',
  'email-preferences',
]

export const STATUS_LABELS = {
  not_configured: { id: 'legal_status_not_configured', defaultMessage: 'Not configured' },
  draft: { id: 'legal_status_draft', defaultMessage: 'Draft' },
  published: { id: 'legal_status_published', defaultMessage: 'Published' },
  draft_changes: { id: 'legal_status_draft_changes', defaultMessage: 'Unpublished changes' },
}

export function statusLabel(status, trans) {
  const item = STATUS_LABELS[status] || STATUS_LABELS.not_configured
  return trans ? trans(item) : item.defaultMessage
}

export function emptyCopy() {
  return { title: '', description: '', html: '' }
}

export function foldCopies(page) {
  const copies = {
    en: { ...emptyCopy(), ...(page?.copies?.en || {}) },
    es: { ...emptyCopy(), ...(page?.copies?.es || {}) },
  }
  const locale = page?.content_locale === 'es' ? 'es' : 'en'
  if (page && (page.content_locale || page.title || page.description || page.html)) {
    copies[locale] = {
      title: page.title || '',
      description: page.description || '',
      html: page.html || '',
    }
  }
  return copies
}

export function copiesFromDraft(draft) {
  const locale = draft?.locale === 'es' ? 'es' : 'en'
  const copies = { en: emptyCopy(), es: emptyCopy() }
  const primary = {
    title: draft?.title || '',
    description: draft?.description || '',
    html: draft?.html || '',
  }
  if (primary.title || primary.description || primary.html) {
    copies[locale] = primary
  }
  ;['en', 'es'].forEach((code) => {
    const pack = draft?.translations?.[code]
    if (!pack) return
    if (String(pack.title || '').trim() || String(pack.html || '').trim()) {
      copies[code] = {
        title: pack.title || '',
        description: pack.description || '',
        html: pack.html || '',
      }
    }
  })
  return copies
}

export function formFromCopies(copies, locale, extra = {}) {
  const code = locale === 'es' ? 'es' : 'en'
  const current = { ...emptyCopy(), ...(copies?.[code] || {}) }
  const form = {
    title: current.title,
    description: current.description,
    html: current.html,
    content_locale: code,
    copies: {
      en: { ...emptyCopy(), ...(copies?.en || {}) },
      es: { ...emptyCopy(), ...(copies?.es || {}) },
    },
  }
  if (extra.company_locale === 'es' || extra.company_locale === 'en') {
    form.company_locale = extra.company_locale
  }
  return form
}

export function viewDraft(draft, locale) {
  const company = draft?.locale === 'es' ? 'es' : 'en'
  return formFromCopies(copiesFromDraft(draft), locale || company, { company_locale: company })
}

export function isDirty(baseline, current) {
  if (!baseline || !current) return false
  return JSON.stringify(foldCopies(baseline)) !== JSON.stringify(foldCopies(current))
}

export function missingCompanyLanguage(page) {
  if (!page?.copies) return null
  const company = page.company_locale === 'es' ? 'es' : 'en'
  const active = page.content_locale === 'es' ? 'es' : 'en'
  if (active === company) return null
  if (hasPublishableContent(foldCopies(page)[company])) return null
  return company
}

export function draftPayload(page, expectedUpdatedAt) {
  const payload = {
    title: page.title,
    description: page.description,
    html: page.html,
    expected_updated_at: expectedUpdatedAt,
  }
  if (page.content_locale === 'es' || page.content_locale === 'en') {
    payload.content_locale = page.content_locale
  }
  if (page.copies) {
    const folded = foldCopies(page)
    const locale = page.content_locale === 'es' ? 'es' : 'en'
    const other = locale === 'es' ? 'en' : 'es'
    payload.title = folded[locale].title
    payload.description = folded[locale].description
    payload.html = folded[locale].html
    payload.content_locale = locale
    payload.translations = hasPublishableContent(folded[other]) ? { [other]: folded[other] } : {}
    return payload
  }
  if (page.translations && typeof page.translations === 'object') {
    payload.translations = page.translations
  }
  return payload
}

export function publishPayload(expectedUpdatedAt) {
  return { expected_updated_at: expectedUpdatedAt }
}

export function hasPublishableContent(form) {
  const title = String(form?.title || '').trim()
  const text = String(form?.html || '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()
  return title.length >= 3 && text.length > 0
}
